import isNil from 'lodash/isNil';
import {AttrsSchema, Component} from 'panel';

import {Section} from 'common/components/items-menu/types';
import {getRecentItems, PROMPT_ALLOW_STORAGE_STRING, removeRecentItem} from 'common/report/util/search';
import {RecentSearchItem, RecentSearchItemType} from 'common/report/util/search/types';
import {ENTER_KEY_CODE, KEY_VALUES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {truncate} from 'common/util/string';

import css from './index.styl?inline';
import template from './index.jade';
import {OptionsBarButtonOption, OptionsBarOption, OptionsType} from './types';

interface OptionsBarJsxProps {
  attrs: OptionsBarAttrs;
  on: {
    clickItem?: (ev: CustomEvent) => void;
    updateInput?: (ev: CustomEvent<{track?: boolean; value: string}>) => void;
    submitSearch?: (ev: CustomEvent) => void;
    slideChange?: (ev: CustomEvent) => void;
    resetSections?: (ev: CustomEvent) => void;
    clickButton?: (ev: CustomEvent<string>) => void;
  };
}

interface OptionsBarState {
  allowRecentItems: boolean;
  recentSearchItems: RecentSearchItem[];
  searchInputEl: null | HTMLElement;
  searchText: string;
  showDropdown: boolean;
  slidOutSectionIdx: number | null;
}

interface OptionsBarAttrs {
  options: OptionsBarOption[];
  'search-placeholder': string;
  'search-text': string;
}

const DEFAULT_INPUT_STRING = ``;

export class OptionsBar extends Component<OptionsBarState, OptionsBarAttrs> {
  static get attrsSchema(): AttrsSchema<OptionsBarAttrs> {
    return {
      options: {type: `json`, tsType: `OptionsBarOption[]`},
      'search-placeholder': `string`,
      'search-text': `string`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        allowRecentItems: true,
        recentSearchItems: [],
        searchInputEl: null,
        searchText: DEFAULT_INPUT_STRING,
        showDropdown: false,
        slidOutSectionIdx: null,
      },
      helpers: {
        OptionsType,
        DEFAULT_INPUT_STRING,
        isNil,
        handleButtonClick: (buttonConfig: OptionsBarButtonOption) => {
          this.dispatchEvent(
            new CustomEvent(`clickButton`, {detail: {name: buttonConfig.name, eventName: buttonConfig.eventName}}),
          );
        },
        handleClickItemFromOption: ({detail}) => {
          this.dispatchEvent(
            new CustomEvent(`clickItem`, {
              detail: {...detail.item},
            }),
          );
        },
        handleSearchItemSelection: (value) => {
          this.update({
            searchText: value,
            showDropdown: false,
          });
          this.dispatchEvent(new CustomEvent(`updateInput`, {detail: {track: true, value}}));
        },
        handleInputClick: () => this.update({showDropdown: true}),
        handleInputDropdownChange: (detail) => {
          if (detail.state === `closed`) {
            this.update({showDropdown: false});
          }
        },
        handleKeydown: (ev) => {
          const pressedEnter = ev.keyCode === ENTER_KEY_CODE || ev.code === KEY_VALUES.ENTER;
          if (pressedEnter) {
            this.dispatchEvent(new CustomEvent(`submitSearch`));
          }
          this.update({showDropdown: !pressedEnter});
        },
        handleUpdateInput: (value) => {
          this.update({recentSearchItems: getRecentItems(), searchText: value});
          this.dispatchEvent(new CustomEvent(`updateInput`, {detail: {value}}));
        },
        inputInserted: (vnode) => this.update({searchInputEl: vnode.elm}),
        getTotalNumberOfSelectedItems: (sections: Section[]) =>
          sections.reduce((c, {items}) => (items ? c + items.filter((item) => item.isSelected).length : 0), 0),
        handleSlideOut: (idx: number) => {
          const {slidOutSectionIdx} = this.state;
          const isOpen = !isNil(slidOutSectionIdx);
          this.dispatchEvent(new CustomEvent(`slideChange`, {detail: {open: !isOpen}}));
          this.update({slidOutSectionIdx: isOpen ? null : idx});
        },
        resetSectionsSlideOut: () => {
          this.dispatchEvent(new CustomEvent(`resetSections`));
        },
        sidePanelSections: () => this.attr(`options`).filter((option) => option.type === OptionsType.Sidebar),
        getSearchedHistory: () =>
          this.state.recentSearchItems
            .filter(
              (item) => item.type === RecentSearchItemType.SearchTerm && item.label.includes(this.state.searchText),
            )
            .map(
              (item): RecentSearchItem => ({
                icon: item.icon,
                key: item.key,
                label: truncate(item.label, 50),
                link: item.link,
                type: item.type,
                value: item.label,
              }),
            ),
        getSearchedHistorySections: ($mixins) => [
          {
            actionsRenderer: (item) => $mixins[`action-renderer`](item),
            items: this.helpers.getSearchedHistory().slice(0, 5),
          },
        ],
        handleClickRemove: (ev, item) => {
          ev.stopPropagation();
          removeRecentItem(item);
          this.update({recentSearchItems: getRecentItems()});
        },
        getEmptyState: () => (this.state.allowRecentItems ? `No recent history` : PROMPT_ALLOW_STORAGE_STRING),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.update({recentSearchItems: getRecentItems()});
    this.updateAllowStorage();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `search-text`) {
      this.update({searchText: this.attr(`search-text`)});
    }
  }

  public focus() {
    const {searchInputEl} = this.state;
    if (!searchInputEl) {
      return;
    }

    requestAnimationFrame(() => {
      searchInputEl.focus();
    });
  }

  private async updateAllowStorage() {
    await window.gdprData?.waitUntilReady();
    this.update({allowRecentItems: window.gdprData?.allowStorage});
  }

  public set searchText(searchText: string) {
    this.update({searchText});
  }
}

export const OptionsBarEl = defineAndMakeJsxFactory<OptionsBarJsxProps>(`load-entity-options-bar`, OptionsBar);
